import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Check, Crown, Zap, AlertCircle } from 'lucide-react';
import { getBillingRole } from '../utils/billingRole';

const Subscription = () => {
  const navigate = useNavigate();
  const { user, API, language } = useContext(AuthContext);
  const billingRole = getBillingRole(user);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/status`, { params: { role: billingRole } });
      setStatus(response.data);
    } catch (error) {
      toast.error('Failed to load subscription status');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = (plan) => {
    // Navigate to the new Saferpay subscription page
    navigate(`/subscription-payment${billingRole === 'professional' ? '?billing_role=professional' : ''}`, { state: { plan } });
  };

  const handleCancel = async () => {
    const confirmMsg = {
      en: 'Are you sure you want to cancel your subscription?',
      de: 'Möchten Sie Ihr Abonnement wirklich kündigen?',
      it: 'Sei sicuro di voler annullare il tuo abbonamento?'
    };
    const successMsg = {
      en: 'Subscription will end at period end',
      de: 'Abonnement endet am Ende des Zeitraums',
      it: 'L\'abbonamento terminerà alla fine del periodo'
    };
    
    if (!window.confirm(confirmMsg[language] || confirmMsg.en)) {
      return;
    }

    setCancelling(true);
    try {
      await axios.post(`${API}/subscriptions/cancel`, {}, { params: { role: billingRole } });
      toast.success(successMsg[language] || successMsg.en);
      fetchSubscriptionStatus();
    } catch (error) {
      toast.error('Cancellation failed');
    } finally {
      setCancelling(false);
    }
  };

  const content = {
    en: {
      title: 'Subscription',
      trialBadge: 'Free Trial',
      daysRemaining: 'days remaining',
      currentPlan: 'Current Plan',
      choosePlan: 'Yearly Subscription',
      monthly: 'Monthly',
      annual: 'Annual',
      perMonth: '/month',
      perYear: '/year',
      save: 'Save 28%',
      subscribe: 'Subscribe',
      cancel: 'Cancel Subscription',
      features: 'Features included:',
      feature1: 'Unlimited job applications',
      feature2: 'Profile verification badge',
      feature3: 'AI-powered job matching',
      feature4: 'Priority support',
      feature5: 'Advanced analytics',
      trialEnding: 'Your free trial is ending soon!',
      selectPlan: 'Select a plan to continue using Helply after your trial ends.',
      paidThrough: 'Paid access until:',
      cancelledPaidThrough: 'Renewal cancelled',
      back: 'Back'
    },
    de: {
      title: 'Abonnement',
      trialBadge: 'Kostenlose Testversion',
      daysRemaining: 'Tage verbleibend',
      currentPlan: 'Aktueller Plan',
      choosePlan: 'Jahresabonnement',
      monthly: 'Monatlich',
      annual: 'Jährlich',
      perMonth: '/Monat',
      perYear: '/Jahr',
      save: '28% sparen',
      subscribe: 'Abonnieren',
      cancel: 'Abonnement kündigen',
      features: 'Enthaltene Funktionen:',
      feature1: 'Unbegrenzte Jobbewerbungen',
      feature2: 'Profilverifizierungsabzeichen',
      feature3: 'KI-gestütztes Job-Matching',
      feature4: 'Prioritäts-Support',
      feature5: 'Erweiterte Analysen',
      trialEnding: 'Ihre kostenlose Testversion endet bald!',
      selectPlan: 'Wählen Sie einen Plan, um Helply nach Ablauf der Testversion weiter zu nutzen.',
      paidThrough: 'Bezahlter Zugang bis:',
      cancelledPaidThrough: 'Verlängerung gekündigt',
      back: 'Zurück'
    },
    it: {
      title: 'Abbonamento',
      trialBadge: 'Prova Gratuita',
      daysRemaining: 'giorni rimanenti',
      currentPlan: 'Piano Attuale',
      choosePlan: 'Abbonamento annuale',
      monthly: 'Mensile',
      annual: 'Annuale',
      perMonth: '/mese',
      perYear: '/anno',
      save: 'Risparmia 28%',
      subscribe: 'Abbonati',
      cancel: 'Annulla Abbonamento',
      features: 'Funzionalità incluse:',
      feature1: 'Candidature illimitate',
      feature2: 'Badge di verifica profilo',
      feature3: 'Matching ordini con IA',
      feature4: 'Supporto prioritario',
      feature5: 'Analisi avanzate',
      trialEnding: 'La tua prova gratuita sta per terminare!',
      selectPlan: 'Seleziona un piano per continuare a usare Helply dopo la fine della prova.',
      paidThrough: 'Accesso pagato fino al:',
      cancelledPaidThrough: 'Rinnovo annullato',
      back: 'Indietro'
    },
    fr: {
      title: 'Abonnement',
      trialBadge: 'Essai Gratuit',
      daysRemaining: 'jours restants',
      currentPlan: 'Plan Actuel',
      choosePlan: 'Abonnement annuel',
      monthly: 'Mensuel',
      annual: 'Annuel',
      perMonth: '/mois',
      perYear: '/an',
      save: 'Économisez 28%',
      subscribe: 'S\'abonner',
      cancel: 'Annuler l\'Abonnement',
      features: 'Fonctionnalités incluses:',
      feature1: 'Candidatures illimitées',
      feature2: 'Badge de vérification du profil',
      feature3: 'Matching d\'emplois par IA',
      feature4: 'Support prioritaire',
      feature5: 'Analyses avancées',
      trialEnding: 'Votre essai gratuit se termine bientôt!',
      selectPlan: 'Sélectionnez un plan pour continuer à utiliser Helply après la fin de votre essai.',
      paidThrough: 'Accès payé jusqu’au :',
      cancelledPaidThrough: 'Renouvellement annulé',
      back: 'Retour'
    }
  };

  const t = content[language] || content.en;
  const dateLocale = { en: 'en-CH', de: 'de-CH', it: 'it-CH', fr: 'fr-CH' }[language] || 'en-CH';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="subscription-page">
      <div className="max-w-6xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t.back}
        </Button>

        <h1 className="text-3xl font-bold font-heading mb-2">{t.title}</h1>

        {/* Current Status */}
        {status && (
          <Card className="mb-8">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="font-heading">{t.currentPlan}</CardTitle>
                {status.status === 'trial' && (
                  <Badge className="bg-green-100 text-green-800">
                    {t.trialBadge}
                  </Badge>
                )}
                {status.status === 'active' && (
                  <Badge className="bg-blue-100 text-blue-800">
                    {status.plan === 'monthly' ? t.monthly : t.annual}
                  </Badge>
                )}
                {status.status === 'cancelled' && status.is_active && (
                  <Badge className="bg-amber-100 text-amber-800" data-testid="cancelled-paid-through-badge">
                    {t.cancelledPaidThrough}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {status.status === 'trial' && status.trial_days_remaining !== null && (
                <div>
                  <p className="text-2xl font-bold text-blue-600">
                    {status.trial_days_remaining} {t.daysRemaining}
                  </p>
                  {status.trial_days_remaining <= 7 && (
                    <Alert className="mt-4 border-orange-200 bg-orange-50">
                      <AlertCircle className="h-4 w-4 text-orange-600" />
                      <AlertDescription className="text-orange-800">
                        <strong>{t.trialEnding}</strong>
                        <br />
                        {t.selectPlan}
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
              {status.status === 'active' && (
                <div>
                  <p className="text-sm text-gray-600">
                    {language === 'en' ? 'Active until:' : language === 'it' ? 'Attivo fino a:' : 'Aktiv bis:'}{' '}
                    {new Date(status.subscription_ends_at).toLocaleDateString()}
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-4 text-red-600 hover:text-red-700"
                    onClick={handleCancel}
                    disabled={cancelling}
                    data-testid="cancel-subscription-btn"
                  >
                    {cancelling ? '...' : t.cancel}
                  </Button>
                </div>
              )}
              {status.status === 'cancelled' && status.is_active && (
                <p className="text-sm text-gray-700" data-testid="cancelled-paid-through-date">
                  {t.paidThrough} {new Date(status.subscription_ends_at).toLocaleDateString(dateLocale)}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Plans — Yearly Only */}
        {(!status || !status.is_active) && (
          <>
            <h2 className="text-2xl font-bold font-heading mb-6">{t.choosePlan}</h2>
            <div className="max-w-md mx-auto">
              {/* Yearly Plan — role-based price */}
              <Card className="hover-lift cursor-pointer border-2 border-blue-600 relative" data-testid="annual-plan-card">
                <div className="absolute -top-3 right-4">
                  <Badge className="bg-green-600 text-white">Best Value</Badge>
                </div>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-2xl font-heading flex items-center">
                        {t.annual}
                        <Crown className="w-5 h-5 ml-2 text-yellow-600" />
                      </CardTitle>
                      <CardDescription className="mt-2">
                        <span className="text-5xl font-bold text-gray-900">
                          {billingRole === 'client' ? '12' : '99'} CHF
                        </span>
                        <span className="text-gray-500">{t.perYear}</span>
                        <br />
                        <span className="text-sm text-blue-600">
                          {billingRole === 'client' ? '= CHF 1.00/month' : '≈ CHF 8.25/month'}
                        </span>
                      </CardDescription>
                    </div>
                    <Crown className="w-8 h-8 text-yellow-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700 mb-4"
                    onClick={() => handleSubscribe('yearly')}
                    data-testid="subscribe-annual-btn"
                  >
                    {t.subscribe} — CHF {billingRole === 'client' ? '12' : '99'}{t.perYear}
                  </Button>
                  <div className="space-y-2">
                    <p className="font-medium text-sm">{t.features}</p>
                    <ul className="space-y-2 text-sm">
                      {[t.feature1, t.feature2, t.feature3, t.feature4, t.feature5].map((feature, idx) => (
                        <li key={idx} className="flex items-center space-x-2">
                          <Check className="w-4 h-4 text-green-600" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Worldline Info */}
            <div className="mt-6 text-center text-sm text-gray-500">
              <p>🔒 Secure payment powered by Worldline</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Subscription;
